import { Box, List, ListItem, ListItemText, Typography, alpha, useTheme } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { ConfirmDialog } from "../../shared/ui";

interface Props {
  open: boolean;
  sectionRejectionLabels: string[];
  narrativeCommentCount: number;
  generalCommentCount: number;
  /**
   * Count of existing top-level unresolved comments from the current cycle.
   * When > 0 the CO can confirm re-rejection even with no newly-staged
   * comments — typical post-retract flow where the same feedback gets sent
   * back to the officer without typing anything new.
   */
  existingUnresolvedCount?: number;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Final confirmation modal shown before the CO's Reject submission goes through. Replaces
 * the older drawer that mixed editing + submission in one surface. All editing now happens
 * on the main CO Review page; this dialog only summarizes what's staged and confirms.
 */
export function RejectConfirmationDialog({
  open,
  sectionRejectionLabels,
  narrativeCommentCount,
  generalCommentCount,
  existingUnresolvedCount = 0,
  onClose,
  onConfirm,
  loading,
}: Props) {
  const theme = useTheme();
  const stagedTotal =
    sectionRejectionLabels.length + narrativeCommentCount + generalCommentCount;
  const total = stagedTotal + existingUnresolvedCount;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      tone="warning"
      confirmColor="error"
      icon={<ReportProblemOutlinedIcon />}
      confirmStartIcon={<ReportProblemOutlinedIcon />}
      maxWidth={480}
      title="Reject report?"
      description={
        <>
          The report will be sent back to the officer with your feedback.
          They&apos;ll revise and resubmit for your review.
        </>
      }
      confirmLabel="Reject & send back"
      loading={loading}
      confirmDisabled={total === 0}
    >
      <Box
        sx={{
          mt: 3,
          p: 1.75,
          borderRadius: 2,
          border: `1px solid ${theme.palette.warning.main}`,
          bgcolor: alpha(theme.palette.warning.main, 0.06),
          textAlign: "left",
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          Sending {total} comment{total === 1 ? "" : "s"}
        </Typography>
        <List dense disablePadding>
          {sectionRejectionLabels.length > 0 && (
            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={`${sectionRejectionLabels.length} section rejection${sectionRejectionLabels.length === 1 ? "" : "s"}`}
                secondary={sectionRejectionLabels.join(", ")}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItem>
          )}
          {narrativeCommentCount > 0 && (
            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={`${narrativeCommentCount} narrative inline comment${narrativeCommentCount === 1 ? "" : "s"}`}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              />
            </ListItem>
          )}
          {generalCommentCount > 0 && (
            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={`${generalCommentCount} general comment${generalCommentCount === 1 ? "" : "s"}`}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              />
            </ListItem>
          )}
          {existingUnresolvedCount > 0 && (
            <ListItem disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={`${existingUnresolvedCount} existing unresolved comment${existingUnresolvedCount === 1 ? "" : "s"}`}
                secondary="Previously written; officer will see them again."
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItem>
          )}
        </List>
      </Box>
    </ConfirmDialog>
  );
}
